import { Fragment, useEffect, useState } from 'react';
import { Laterale } from '../components/Laterale.jsx';
import {
  listerUtilisateurs,
  listerVoyagesUtilisateur,
  desactiverCompte,
  reactiverCompte,
} from '../lib/admin.js';
import { formaterDate, resumerPeriodeCourte } from '../lib/format.js';
import { libelleStatut } from '../lib/trips.js';

/** Voyages du compte déplié, chargés à la demande et mis en cache par compte. */
function VoyagesUtilisateur({ userId }) {
  const [voyages, setVoyages] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let annule = false;

    listerVoyagesUtilisateur(userId)
      .then((liste) => !annule && setVoyages(liste))
      .catch((echec) => !annule && setErreur(echec.message));

    return () => {
      annule = true;
    };
  }, [userId]);

  if (erreur) return <p className="erreur" role="alert">{erreur}</p>;
  if (voyages === null) return <p role="status">Chargement…</p>;
  if (voyages.length === 0) return <p className="aide">Aucun voyage pour ce compte.</p>;

  return (
    <table className="tableau-admin tableau-admin--imbrique">
      <thead>
        <tr>
          <th scope="col">Voyage</th>
          <th scope="col">Destination</th>
          <th scope="col">Dates</th>
          <th scope="col">Statut</th>
          <th scope="col">Appels IA</th>
        </tr>
      </thead>
      <tbody>
        {voyages.map((voyage) => (
          <tr key={voyage.id}>
            <td>{voyage.name}</td>
            <td>{voyage.destination.label}</td>
            <td>{resumerPeriodeCourte(voyage)}</td>
            <td>{libelleStatut(voyage.status)}</td>
            <td>{voyage.aiCallCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Admin() {
  const [utilisateurs, setUtilisateurs] = useState(null);
  const [stats, setStats] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [deplie, setDeplie] = useState(null);
  const [enCours, setEnCours] = useState(null);

  useEffect(() => {
    let annule = false;

    listerUtilisateurs()
      .then((data) => {
        if (annule) return;
        setUtilisateurs(data.users);
        setStats(data.stats);
      })
      .catch((echec) => !annule && setErreur(echec.message));

    return () => {
      annule = true;
    };
  }, []);

  /** Ne remplace que `disabledAt` : la réponse du serveur ne porte pas les
   * compteurs de voyages et d'appels déjà connus, les écraser les remettrait
   * à zéro à l'écran. */
  async function basculerActivation(utilisateur) {
    setErreur(null);
    setEnCours(utilisateur.id);

    try {
      const bascule = utilisateur.disabledAt ? reactiverCompte : desactiverCompte;
      const { disabledAt } = await bascule(utilisateur.id);
      setUtilisateurs((precedent) =>
        precedent.map((u) => (u.id === utilisateur.id ? { ...u, disabledAt } : u)));
    } catch (echec) {
      setErreur(echec.message);
    } finally {
      setEnCours(null);
    }
  }

  return (
    <div className="cadre">
      <Laterale actif="admin" />

      <main>
        <header className="entete">
          <div>
            <h1>Administration</h1>
            <p className="entete__sous-titre">Comptes inscrits et charge de génération IA.</p>
          </div>
        </header>

        {erreur && (
          <p className="erreur" role="alert">
            {erreur}
          </p>
        )}

        {stats && (
          <dl className="stats-admin">
            <div>
              <dt>Comptes</dt>
              <dd>{stats.totalUsers}</dd>
            </div>
            <div>
              <dt>Voyages</dt>
              <dd>{stats.totalTrips}</dd>
            </div>
            <div>
              <dt>Appels IA (total)</dt>
              <dd>{stats.totalAiCalls}</dd>
            </div>
            <div>
              <dt>Appels IA (24h)</dt>
              <dd>{stats.aiCalls24h}</dd>
            </div>
            <div>
              <dt>Appels IA (7 j)</dt>
              <dd>{stats.aiCalls7j}</dd>
            </div>
          </dl>
        )}

        {utilisateurs === null && !erreur && <p role="status">Chargement…</p>}

        {utilisateurs?.length > 0 && (
          <table className="tableau-admin">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Prénom</th>
                <th scope="col">Inscrit le</th>
                <th scope="col">Voyages</th>
                <th scope="col">Appels IA</th>
                <th scope="col">Statut</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {utilisateurs.map((utilisateur) => (
                <Fragment key={utilisateur.id}>
                  <tr>
                    <td>
                      <button
                        type="button"
                        className="tableau-admin__bascule"
                        onClick={() => setDeplie((precedent) =>
                          precedent === utilisateur.id ? null : utilisateur.id)}
                        aria-expanded={deplie === utilisateur.id}
                      >
                        {utilisateur.email}
                      </button>
                    </td>
                    <td>{utilisateur.firstName}</td>
                    <td>{formaterDate(utilisateur.createdAt)}</td>
                    <td>{utilisateur.tripCount}</td>
                    <td>{utilisateur.aiCallCount}</td>
                    <td>
                      <span className={`badge${utilisateur.disabledAt ? ' badge--desactive' : ''}`}>
                        {utilisateur.disabledAt ? 'Désactivé' : 'Actif'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="bouton bouton--discret bouton--auto"
                        disabled={enCours === utilisateur.id}
                        onClick={() => basculerActivation(utilisateur)}
                      >
                        {utilisateur.disabledAt ? 'Réactiver' : 'Désactiver'}
                      </button>
                    </td>
                  </tr>
                  {deplie === utilisateur.id && (
                    <tr>
                      <td colSpan={7}>
                        <VoyagesUtilisateur userId={utilisateur.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}
